import { supabase } from '@/integrations/supabase/client';
import { contentManagementService } from './contentManagementService';

export interface SMSMessage {
  to: string;
  message: string;
}

export interface SMSResponse {
  success: boolean;
  messageId?: string;
  error?: string;
  cost?: number;
}

class SMSService {
  /**
   * Every SMS goes through the send-sms edge function, which holds the Nsano
   * key. Free-text messages are for staff only; customers' browsers can only
   * ask for the fixed order-confirmation and welcome messages.
   */
  private async invoke(body: Record<string, unknown>): Promise<SMSResponse> {
    try {
      const { data, error } = await supabase.functions.invoke('send-sms', { body });
      if (error) return { success: false, error: error.message };
      if (!data?.success) return { success: false, error: data?.error || 'Failed to send SMS' };
      return { success: true, messageId: data.message_id };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Network error' };
    }
  }

  /**
   * Format phone number for Ghana (+233)
   */
  private formatPhoneNumber(phoneNumber: string): string {
    // Remove any non-digit characters
    let cleaned = phoneNumber.replace(/\D/g, '');
    
    // If it starts with 0, replace with 233
    if (cleaned.startsWith('0')) {
      cleaned = '233' + cleaned.slice(1);
    }
    
    // If it doesn't start with 233, assume it's missing country code
    if (!cleaned.startsWith('233')) {
      return '+' + cleaned;
    }
    
    return '+' + cleaned;
  }

  /**
   * Send single SMS via Supabase Edge Function
   */
  async sendSMS(to: string, message: string, orderId?: string): Promise<SMSResponse> {
    try {
      // Format the phone number first
      const formattedTo = this.formatPhoneNumber(to);
      
      // Validate phone number before sending
      if (!this.isValidPhoneNumber(to)) {
        console.error('Invalid phone number format:', { original: to, formatted: formattedTo });
        return {
          success: false,
          error: 'Invalid phone number format. Please use a valid Ghanaian number.'
        };
      }
      
      // Log the SMS sending attempt with more details
      console.log('SMS Service - Sending message:', {
        to: to,
        formattedTo: formattedTo,
        messageLength: message.length,
        orderId: orderId || 'N/A',
        timestamp: new Date().toISOString()
      });
      
      return await this.invoke({ to: formattedTo, message, orderId });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error('SMS Service - Failed to send SMS:', {
        error: errorMessage,
        to: to,
        hasOrderId: !!orderId
      });
      
      return {
        success: false,
        error: `Failed to send SMS: ${errorMessage}`
      };
    }
  }

  /**
   * Ask for the confirmation SMS of an order just placed. The function builds
   * the message and sends it to the order's customer, once.
   */
  async sendOrderConfirmation(
    _phoneNumber: string,
    orderNumber: string,
    _customerName?: string,
    _total?: number,
    _deliveryTime?: string
  ): Promise<SMSResponse> {
    return this.invoke({ type: 'order_confirmation', orderNumber });
  }

  /**
   * Welcome SMS to the signed-in user's own phone, just after signing up
   */
  async sendWelcome(): Promise<SMSResponse> {
    return this.invoke({ type: 'welcome' });
  }

  /**
   * Send order status update SMS
   */
  async sendOrderStatusUpdate(
    phoneNumber: string,
    orderNumber: string,
    status: string,
    customerName: string
  ): Promise<SMSResponse> {
    const message = this.generateStatusUpdateMessage(orderNumber, status, customerName);
    return this.sendSMS(phoneNumber, message);
  }

  /**
   * Send order ready for pickup SMS using template
   */
  async sendOrderReadyNotification(
    phoneNumber: string,
    orderNumber: string,
    customerName: string,
    fulfillmentType: string = 'pickup/delivery'
  ): Promise<SMSResponse> {
    try {
      const message = await this.template('order_ready', {
        customerName,
        orderNumber,
        fulfillmentType
      });

      if (!message) {
        // Fallback to hardcoded message
        const fallbackMessage = `Hi ${customerName}!\n\nYour order #${orderNumber} is ready for ${fulfillmentType}.\n\nThank you for choosing Dough Man Foods! 🍩`;
        return this.sendSMS(phoneNumber, fallbackMessage);
      }

      return this.sendSMS(phoneNumber, message);
    } catch (error) {
      console.error('Error processing SMS template:', error);
      // Fallback to hardcoded message
      const fallbackMessage = `Hi ${customerName}!\n\nYour order #${orderNumber} is ready for ${fulfillmentType}.\n\nThank you for choosing Dough Man Foods! 🍩`;
      return this.sendSMS(phoneNumber, fallbackMessage);
    }
  }

  /**
   * Send order delivered SMS using template
   */
  async sendOrderDeliveredNotification(
    phoneNumber: string,
    orderNumber: string,
    customerName: string
  ): Promise<SMSResponse> {
    try {
      const message = await this.template('order_delivered', {
        customerName,
        orderNumber
      });

      if (!message) {
        // Fallback to hardcoded message
        const fallbackMessage = `Hi ${customerName}!\n\nYour order #${orderNumber} has been delivered.\n\nEnjoy your meal!\nThanks for choosing Dough Man Foods! 🍩`;
        return this.sendSMS(phoneNumber, fallbackMessage);
      }

      return this.sendSMS(phoneNumber, message);
    } catch (error) {
      console.error('Error processing SMS template:', error);
      // Fallback to hardcoded message
      const fallbackMessage = `Hi ${customerName}!\n\nYour order #${orderNumber} has been delivered.\n\nEnjoy your meal!\nThanks for choosing Dough Man Foods! 🍩`;
      return this.sendSMS(phoneNumber, fallbackMessage);
    }
  }

  /**
   * Send promotional SMS using template
   */
  async sendPromotion(
    phoneNumber: string,
    customerName: string,
    promoMessage: string
  ): Promise<SMSResponse> {
    try {
      const message = await this.template('promotional', {
        customerName,
        promoMessage
      });

      if (!message) {
        // Fallback to hardcoded message
        const fallbackMessage = `Hi ${customerName}!\n\n${promoMessage}\n\n- Dough Man Foods`;
        return this.sendSMS(phoneNumber, fallbackMessage);
      }

      return this.sendSMS(phoneNumber, message);
    } catch (error) {
      console.error('Error processing SMS template:', error);
      // Fallback to hardcoded message
      const fallbackMessage = `Hi ${customerName}!\n\n${promoMessage}\n\n- Dough Man Foods`;
      return this.sendSMS(phoneNumber, fallbackMessage);
    }
  }

  /**
   * Send Ghana celebration SMS using template
   */
  async sendGhanaCelebration(
    phoneNumber: string,
    customerName: string,
    celebrationMessage: string
  ): Promise<SMSResponse> {
    try {
      const message = await this.template('ghana_celebration', {
        customerName,
        celebrationMessage
      });

      if (!message) {
        // Fallback to hardcoded message
        const fallbackMessage = `Chaley ${customerName}! 🎉\n\n${celebrationMessage}\n\nYou force! Time to celebrate with our special packages.\n\n- Dough Man Foods`;
        return this.sendSMS(phoneNumber, fallbackMessage);
      }

      return this.sendSMS(phoneNumber, message);
    } catch (error) {
      console.error('Error processing SMS template:', error);
      // Fallback to hardcoded message
      const fallbackMessage = `Chaley ${customerName}! 🎉\n\n${celebrationMessage}\n\nYou force! Time to celebrate with our special packages.\n\n- Dough Man Foods`;
      return this.sendSMS(phoneNumber, fallbackMessage);
    }
  }

  private template(key: string, variables: Record<string, unknown>): Promise<string | null> {
    return contentManagementService.processSMSTemplate(key, variables);
  }

  /**
   * Generate status update message
   */
  private generateStatusUpdateMessage(
    orderNumber: string,
    status: string,
    customerName: string
  ): string {
    const statusMessages: Record<string, string> = {
      'assigned': 'has been assigned to a rider',
      'confirmed': 'has been confirmed and is being prepared',
      'preparing': 'is being prepared in our kitchen',
      'ready': 'is ready for pickup/delivery',
      'picked_up': 'has been picked up by the rider',
      'in_transit': 'is on its way to you',
      'out_for_delivery': 'is out for delivery',
      'delivered': 'has been delivered successfully',
      'cancelled': 'has been cancelled',
      'dispatched': 'has been dispatched for delivery'
    };

    const statusText = statusMessages[status.toLowerCase()] || status.toLowerCase();
    
    return `Hi ${customerName}!\n\nYour order #${orderNumber} ${statusText}.\n\n- Dough Man Foods`;
  }

  /**
   * Validate phone number format for Ghanaian numbers
   * Supports various formats:
   * - Local: 0271234567
   * - International: +233271234567
   * - With dashes/spaces: 027-123-4567, 027 123 4567
   * - With country code: 233271234567
   */
  isValidPhoneNumber(phoneNumber: string): boolean {
    if (!phoneNumber) return false;
    
    // Remove all non-digit characters
    const cleaned = phoneNumber.replace(/\D/g, '');
    
    // Check for valid Ghana mobile number lengths
    if (cleaned.length === 10) {
      // Local format (starts with 0)
      if (cleaned.startsWith('0')) {
        const prefix = cleaned.substring(1, 3);
        const validPrefixes = ['20', '21', '24', '25', '26', '27', '28', '29', '50', '51', '52', '53', '54', '55', '56', '57', '59'];
        return validPrefixes.includes(prefix);
      }
      return false;
    } 
    
    // International format (starts with 233)
    if (cleaned.length === 12 && cleaned.startsWith('233')) {
      const prefix = cleaned.substring(3, 5);
      const validPrefixes = ['20', '21', '24', '25', '26', '27', '28', '29', '50', '51', '52', '53', '54', '55', '56', '57', '59'];
      return validPrefixes.includes(prefix);
    }
    
    // E.164 format (starts with +233)
    if (phoneNumber.startsWith('+233') && cleaned.length === 12) {
      const prefix = cleaned.substring(3, 5);
      const validPrefixes = ['20', '21', '24', '25', '26', '27', '28', '29', '50', '51', '52', '53', '54', '55', '56', '57', '59'];
      return validPrefixes.includes(prefix);
    }
    
    return false;
  }

  /**
   * Get SMS cost estimate
   */
  estimateCost(messageCount: number): number {
    // Rough estimate - actual cost depends on provider and volume
    const costPerSMS = 0.05; // GH₵ 0.05 per SMS
    return messageCount * costPerSMS;
  }
}

export const smsService = new SMSService();

export default smsService;